import PropTypes from 'prop-types';
import { useFieldArray, useFormContext } from 'react-hook-form';
// @mui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Iconify from 'src/components/iconify';

import RHFTextField from './rhf-text-field';
import { RHFSelect } from './rhf-select';
import { RHFCheckbox } from './rhf-checkbox';

/**
 * Reusable repeatable component editor backed by react-hook-form's `useFieldArray`.
 * Renders cards with add / remove / move-up / move-down controls and applies a
 * field schema (`fields` prop) to each row.
 *
 * Each entry in `fields` is `{ name, label, type, multiline, minRows, options, helperText, gridSpan }`.
 * Supported `type`s: `text` (default), `number`, `select`, `textarea`, `checkbox`.
 */
export default function RepeatableSection({
  name,
  title,
  description,
  fields,
  defaultItem,
  addLabel = 'Add item',
  emptyLabel,
  reorder = true,
}) {
  const { control, formState } = useFormContext();
  const { fields: items, append, remove, move } = useFieldArray({ control, name });

  const error = formState?.errors?.[name];

  const renderField = (field, idx) => {
    const fieldName = `${name}.${idx}.${field.name}`;
    const span = field.gridSpan ?? (field.type === 'textarea' ? 12 : 6);
    if (field.type === 'select') {
      return (
        <Box key={field.name} sx={{ gridColumn: { xs: 'span 12', sm: `span ${span}` } }}>
          <RHFSelect name={fieldName} label={field.label} helperText={field.helperText}>
            <MenuItem value="">
              <em>—</em>
            </MenuItem>
            {(field.options ?? []).map((opt) => (
              <MenuItem key={opt.value ?? opt} value={opt.value ?? opt}>
                {opt.label ?? opt}
              </MenuItem>
            ))}
          </RHFSelect>
        </Box>
      );
    }
    if (field.type === 'checkbox') {
      return (
        <Box key={field.name} sx={{ gridColumn: { xs: 'span 12', sm: `span ${span}` } }}>
          <RHFCheckbox name={fieldName} label={field.label} />
        </Box>
      );
    }
    return (
      <Box key={field.name} sx={{ gridColumn: { xs: 'span 12', sm: `span ${span}` } }}>
        <RHFTextField
          name={fieldName}
          label={field.label}
          type={field.type === 'number' ? 'number' : 'text'}
          multiline={field.type === 'textarea' || !!field.multiline}
          minRows={field.minRows ?? (field.type === 'textarea' ? 3 : undefined)}
          helperText={field.helperText}
          fullWidth
        />
      </Box>
    );
  };

  return (
    <Stack spacing={1.5} sx={{ width: '100%' }}>
      {(title || description) && (
        <Box>
          {title && (
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
          )}
          {description && (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          )}
        </Box>
      )}

      <Stack spacing={1.5}>
        {items.length === 0 && (
          <Paper variant="outlined" sx={{ px: 2, py: 1.5, color: 'text.secondary' }}>
            <Typography variant="body2">{emptyLabel ?? 'No items yet — click Add to get started.'}</Typography>
          </Paper>
        )}

        {items.map((item, idx) => (
          <Paper key={item.id} variant="outlined" sx={{ p: 2, position: 'relative' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                {`Item ${idx + 1}`}
              </Typography>
              <Stack direction="row" spacing={0.5}>
                {reorder && (
                  <>
                    <Tooltip title="Move up">
                      <span>
                        <IconButton size="small" disabled={idx === 0} onClick={() => move(idx, idx - 1)}>
                          <Iconify icon="eva:arrow-ios-upward-fill" width={16} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Move down">
                      <span>
                        <IconButton
                          size="small"
                          disabled={idx === items.length - 1}
                          onClick={() => move(idx, idx + 1)}
                        >
                          <Iconify icon="eva:arrow-ios-downward-fill" width={16} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                )}
                <Tooltip title="Remove">
                  <IconButton size="small" color="error" onClick={() => remove(idx)}>
                    <Iconify icon="eva:close-fill" width={16} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: 'repeat(12, 1fr)',
              }}
            >
              {fields.map((f) => renderField(f, idx))}
            </Box>
          </Paper>
        ))}
      </Stack>

      <Box>
        <Button
          startIcon={<Iconify icon="mingcute:add-line" />}
          variant="outlined"
          onClick={() => append({ ...(defaultItem ?? {}) })}
        >
          {addLabel}
        </Button>
      </Box>

      {!!error && (
        <Typography variant="caption" color="error">
          {Array.isArray(error) ? 'One or more items have errors' : error?.message}
        </Typography>
      )}
    </Stack>
  );
}

RepeatableSection.propTypes = {
  name: PropTypes.string.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string,
      type: PropTypes.oneOf(['text', 'number', 'select', 'textarea', 'checkbox']),
      multiline: PropTypes.bool,
      minRows: PropTypes.number,
      options: PropTypes.array,
      helperText: PropTypes.string,
      gridSpan: PropTypes.number,
    })
  ).isRequired,
  defaultItem: PropTypes.object,
  addLabel: PropTypes.string,
  emptyLabel: PropTypes.string,
  reorder: PropTypes.bool,
};
